import { createContext, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import { useActorRef } from '@xstate/react';
import { useDispatch } from 'react-redux';
import { fromPromise } from 'xstate';

import type { OnboardingStateResponseRead } from '@highlight-cp/highlighter-api';
import { highlighterApi } from '@highlight-cp/highlighter-api';
import { createLoggers, redactPii } from '@highlight-cp/logger';
import { Analytics } from '@highlight-cp/user-analytics';
import { highlighterAccountApi } from 'features/account/highlighter-account-api';
import { onboardingMachine } from 'features/account/onboarding/onboarding-machine';
import { onboardingStateApi } from 'features/account/onboarding/onboarding-state-api';
import type { AppDispatch } from 'store';

const { error } = createLoggers({ tag: 'onboarding-state-machine' });

export type OnboardingActor = ReturnType<typeof useActorRef<typeof onboardingMachine>>;

export const OnboardingMachineContext = createContext<OnboardingActor | null>(null);

export const OnboardingMachineProvider = ({ children }: { children: ReactNode }) => {
  const dispatch = useDispatch<AppDispatch>();
  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;

  const providedMachine = useMemo(
    () =>
      onboardingMachine.provide({
        actions: {
          logUnknownOnboardingState: ({ event }) => {
            const output = (event as { output?: OnboardingStateResponseRead }).output;
            if (!output) return;

            fetch('/api/log/onboarding-unknown-state', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(redactPii(output)),
            }).catch(() => {
              // fire-and-forget — swallow network errors
            });
          },
        },
        actors: {
          fetchOnboardingState: fromPromise(async () => {
            try {
              const [result] = await Promise.all([
                dispatchRef
                  .current(
                    onboardingStateApi.endpoints.getOnboardingState.initiate(undefined, {
                      forceRefetch: true,
                    }),
                  )
                  .unwrap(),
                dispatchRef
                  .current(
                    highlighterAccountApi.endpoints.getHighlighterAccount.initiate(undefined, {
                      forceRefetch: true,
                    }),
                  )
                  .unwrap(),
              ]);
              return result;
            } catch (err) {
              error('fetchOnboardingState error', err as unknown as Error);
              throw err;
            }
          }),
          activateAccount: fromPromise(async () => {
            const result = await dispatchRef
              .current(highlighterApi.endpoints.postMyHighlighterAccountsActivateAccount.initiate())
              .unwrap();

            if (result.activated) {
              Analytics.track('highlighter_account_activated', {
                appName: 'highlighter-app',
                highlighter_id: result.highlighter?.id ?? '',
                account_status: result.highlighter?.status ?? 'active',
              });
            }

            // Refetch the account so the auth-guard sees the updated status before
            // the machine reaches 'activated' and the page navigates to /home.
            await dispatchRef
              .current(
                highlighterAccountApi.endpoints.getHighlighterAccount.initiate(undefined, {
                  forceRefetch: true,
                }),
              )
              .unwrap();
            return result;
          }),
          acceptTos: fromPromise(async () => {
            return dispatchRef
              .current(highlighterApi.endpoints.postMyHighlighterAccountsAcceptTos.initiate())
              .unwrap();
          }),
        },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const actor = useActorRef(providedMachine);

  return (
    <OnboardingMachineContext.Provider value={actor}>{children}</OnboardingMachineContext.Provider>
  );
};
